'use client';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { cn } from '../../lib/utils';
import { buttonVariants } from './shadcn/button';
import { assertMenuEntries, resolveMenuLook, type MenuItemLook } from './menuActions';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './shadcn/dropdown-menu';

/** One action row. Its icon, default label and tone come from the closed vocabulary (`action`, see
 *  `menuActions.ts`) or, for a domain-specific action, from its own Lucide `icon` / pre-rendered
 *  `iconNode` (e.g. a brand <ModelIcon/>), never both. A row with no icon at all does not type-check and
 *  is refused at render. */
export type ActionMenuItem = {
  /** What this entry IS, where the label is only what it reads as. Two contributors may well name an
   *  action the same, and a menu keyed by wording then makes React reuse one entry's element for the
   *  other's. Left out where a menu's labels are core's own and already distinct. */
  id?: string;
  tone?: 'default' | 'danger';
  /** Present but not selectable — for an action a row genuinely offers and this row has nothing to do it
   *  with, where dropping the item would make the menu's shape depend on the row. */
  disabled?: boolean;
  /** `true` or `false` makes the row a toggle that shows its state with a trailing check and reads as
   *  `menuitemcheckbox`; left out, the row is a plain action. Selecting it still runs `onSelect`. */
  checked?: boolean;
  onSelect: () => void;
  /** Run only after Radix has closed the menu and restored focus to its stable trigger. Use this when the
   *  action opens another focus-owning overlay, so that overlay captures the trigger rather than a menuitem
   *  that is about to unmount. */
  onAfterClose?: () => void;
} & MenuItemLook;

/** A row that opens a nested panel of its own entries (hover, click, ArrowRight). */
type ActionMenuSubmenu = {
  id?: string;
  disabled?: boolean;
  items: ActionMenuEntry[];
} & Exclude<MenuItemLook, { checked: boolean }>;

/** A divider between groups. `ContextMenu` takes the same literal, so one entry list feeds both menus;
 *  it is defined here because `ContextMenu` converts from this grammar (`toMenuEntries`). */
export const DIVIDER = 'divider' as const;

/** One entry of the menu: an action, a submenu, or the `'divider'` between groups. The same grammar as
 *  `ContextMenu`'s `MenuEntry` (bar its in-place edit row), so one list can feed a kebab and a right-click
 *  menu; `toMenuEntries` converts it. */
export type ActionMenuEntry = ActionMenuItem | ActionMenuSubmenu | typeof DIVIDER;

/** Grace period before a hover-opened menu closes, so a pointer crossing the gap between the trigger
 *  and the panel doesn't dismiss it on the way down. */
const CLOSE_DELAY_MS = 160;

/** Where focus should land when the menu opens. Radix's own answer ("the panel, then its first item if
 *  the user is on the keyboard") is right for a click and for ArrowDown, but wrong for the other two
 *  ways this menu opens: a HOVER must not take focus away from whatever the reader was doing, and
 *  ArrowUp is the menu-button pattern's "open at the last item". */
type OpenFocus = 'none' | 'default' | 'last';

/**
 * Global action menu, composed from the shadcn/ui `DropdownMenu` parts in `./shadcn/dropdown-menu.tsx`.
 *
 * In its default `destructive` shape it opens on hover and stays open while the pointer is over the
 * trigger OR the menu — the panel is a DOM child of the wrapper, so moving down onto an item never
 * leaves it, and a short close delay covers the gap between the two. The trigger is a red trash icon.
 *
 * In the `kebab` shape it is the register's row-actions affordance: a neutral square on the shared icon
 * step that opens on CLICK. Hover is wrong there and not merely a preference — a register row is a thing
 * the pointer crosses on its way somewhere else, and a panel that appears under the cursor on the way past
 * covers the rows below the one being read.
 *
 * The keyboard contract — roving arrows, Home/End, typeahead, Enter/Space, Escape — is Radix's, not
 * this file's. What stays here is the app's policy: hover-to-open with a grace period, ArrowUp opening
 * at the last item, and focus returning to the trigger only when the menu actually held it. `ActionMenu`
 * is handed to plugin bundles through `window.ElowenUiRuntime.components`, so its props are a published
 * contract and did not change with the port.
 */
/** The trigger's shape.
 *
 *  `destructive` is the original: a filled red button for a menu whose whole content is one dangerous
 *  action. `kebab` is the register variant — a neutral square with the three dots, the row-actions
 *  affordance the reference dashboard puts at the end of every row. A row is a thing to READ, and a red
 *  button in each one shouts the single action the reader is least likely to want.
 *
 *  It is an enum rather than a `triggerClassName` string because the two are a pair with `openOnHover`:
 *  a kebab in a register has to be click-only (see below), and a variant is what lets that default
 *  follow the shape instead of every register remembering to pass both.
 *
 *  The radius comes from the shared button primitive (`rounded-md`, 10px), which is the nearest step of
 *  the app's radius scale to the reference's 8 rather than a sixth radius introduced for 2px. */
export type ActionMenuVariant = 'destructive' | 'kebab' | 'accent';

/** The accent trigger is a labelled primary action, click-only like a kebab. Its default/sm steps
 *  are the shared labelled Button steps; `icon` keeps a narrow toolbar's primary action square.
 *
 *  The trigger's shape steps, on the shared button scale (`shadcn/button.tsx`) rather than as heights of
 *  this file's own: `default` is the standard 36px square (44px on a coarse pointer) every neighbouring
 *  control uses, `sm` the compact 32px square for a compact action group inside a card or list row. Both
 *  keep the coarse floor, so the compact group stays whole on a finger too. */
export type ActionMenuSize = 'default' | 'sm' | 'icon';

const TRIGGER_VARIANT = { destructive: 'destructive', kebab: 'ghost', accent: 'default' } as const;

const TRIGGER_SIZE = { default: 'icon', sm: 'icon-sm', icon: 'icon' } as const;

/** What a variant adds on top of its shared button variant: nothing for `destructive`, whose fill and
 *  hover the variant already states, and for the kebab the focus ring and the filled state it keeps while
 *  its menu is open, so the row the panel belongs to is obvious. */
const TRIGGER_TONE: Record<ActionMenuVariant, string> = {
  destructive: '',
  accent: '',
  kebab: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-accent data-[state=open]:text-accent-foreground',
};

export function ActionMenu({ items, label, trigger, triggerClassName, className, testId, align = 'right', variant = 'destructive', size = 'default', openOnHover }: {
  items: ActionMenuEntry[];
  label?: string;
  trigger?: ReactNode;
  /** `data-testid` for the positioning wrapper. A caller whose layout DEPENDS on that wrapper — see
   *  `className` below — has to be able to address it by name; identifying it by the utility classes it
   *  happens to carry makes a test fail on a restyle rather than on the regression it guards. */
  testId?: string;
  /** Override the trigger button styling. Wins over `variant` and `size`. */
  triggerClassName?: string;
  /** Classes for the positioning wrapper — the menu's box in its parent's layout, not the trigger's skin.
   *
   *  It exists for one reason. The wrapper is `position: relative` and block-level, so a caller that puts
   *  the menu in a flex ROW gets an item whose automatic minimum size is its content's min-content width.
   *  A trigger holding nowrap text then refuses to shrink and runs past the end of the row, however
   *  carefully the trigger's own children are set to truncate. Such a caller passes `min-w-0` here. */
  className?: string;
  align?: 'left' | 'right';
  /** The trigger's shape. Defaults to the red destructive button the component shipped with, so no
   *  existing caller changes. */
  variant?: ActionMenuVariant;
  /** The trigger's size step, shared with every other control in the row: `default` is 36px (44px on a
   *  coarse pointer), `sm` the compact 32px square for a compact action group inside a card or list row.
   *  Only the size changes; the variant's colours and the menu's behaviour do not. */
  size?: ActionMenuSize;
  /** `false` makes the menu click/keyboard-only. For a trigger that sits in the reading path — a row the
   *  pointer crosses on its way somewhere else — a hover-opened panel is an interruption, not help.
   *
   *  Left undefined it follows the variant: a `kebab` is a register affordance and is click-only,
   *  everything else keeps the hover behaviour it has always had. A caller may still say either
   *  explicitly, which is what makes this a default rather than a rule. */
  openOnHover?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openFocus = useRef<OpenFocus>('none');
  const afterClose = useRef<(() => void) | null>(null);
  // Whether closing should hand focus back to the trigger. It should when the menu owned focus — a
  // keyboard open, a selection, Escape — and must not when it was merely hovered open, or dismissed by
  // a click somewhere else, because that would steal focus from what the reader actually pressed.
  const restoreFocus = useRef(false);
  const { t } = useTranslation();
  const resolvedLabel = label ?? t.common.actions;
  assertMenuEntries(items, t);
  const hoverOpens = openOnHover ?? variant === 'destructive';

  const cancelClose = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  }, []);
  const openMenu = useCallback((focus: OpenFocus) => {
    cancelClose();
    openFocus.current = focus;
    restoreFocus.current = false;
    setOpen(true);
  }, [cancelClose]);
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => { closeTimer.current = null; setOpen(false); }, CLOSE_DELAY_MS);
  };

  useEffect(() => () => cancelClose(), [cancelClose]);

  return (
    <div
      {...(testId ? { 'data-testid': testId } : {})}
      data-action-menu={variant !== 'accent' && !trigger ? 'icon' : 'labelled'}
      className={className ? `relative ${className}` : 'relative'}
      onMouseEnter={hoverOpens ? () => openMenu('none') : undefined}
      onMouseLeave={hoverOpens ? scheduleClose : undefined}
    >
      <DropdownMenu
        open={open}
        // Not modal: this menu opens on hover, so it must not lock the page's scroll or make the rest
        // of the document unclickable just because a pointer crossed the trigger.
        modal={false}
        onOpenChange={(next) => { cancelClose(); setOpen(next); }}
      >
        <DropdownMenuTrigger
          ref={triggerRef}
          aria-label={resolvedLabel}
          title={resolvedLabel}
          onPointerDown={() => { if (!open) openMenu('default'); }}
          // Radix opens a menu on POINTERDOWN, which a click synthesised by assistive technology or by
          // a script never produces — and this menu has always opened on click. `detail === 0` is that
          // click: a real pointer press carries a click count, so this cannot double-fire with the
          // handler above, and Enter/Space are already consumed by the keyboard branch.
          onClick={(event) => { if (event.detail === 0) (open ? setOpen(false) : openMenu('default')); }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              openMenu('last');
              return;
            }
            if (['ArrowDown', 'Enter', ' '].includes(event.key)) openMenu('default');
          }}
          className={triggerClassName ?? cn(buttonVariants({ variant: TRIGGER_VARIANT[variant], size: variant === 'accent' ? size : TRIGGER_SIZE[size] }), TRIGGER_TONE[variant])}
        >
          {trigger ?? (variant === 'accent' ? resolvedLabel : variant === 'kebab' ? <MoreHorizontal size={16} aria-hidden /> : <Trash2 size={15} aria-hidden />)}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          ref={contentRef}
          align={align === 'right' ? 'end' : 'start'}
          onFocus={() => { restoreFocus.current = true; }}
          onInteractOutside={() => { restoreFocus.current = false; }}
          onOpenAutoFocus={(event) => {
            if (openFocus.current === 'default') return;
            // Radix would focus the panel here; a hover-opened menu must leave focus alone.
            event.preventDefault();
            if (openFocus.current !== 'last') return;
            const rows = contentRef.current?.querySelectorAll<HTMLElement>(':is([role="menuitem"], [role="menuitemcheckbox"]):not([data-disabled])');
            rows?.[rows.length - 1]?.focus();
          }}
          onCloseAutoFocus={(event) => {
            // Radix always returns focus to the trigger; that is wrong for a menu the pointer merely
            // passed over, so the decision is made here instead.
            event.preventDefault();
            if (restoreFocus.current) triggerRef.current?.focus({ preventScroll: true });
            const next = afterClose.current;
            afterClose.current = null;
            next?.();
          }}
        >
          <ActionMenuRows
            items={items}
            choose={(item) => {
              restoreFocus.current = true;
              afterClose.current = item.onAfterClose ?? null;
              item.onSelect();
            }}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** The rows of one panel, recursing into submenus. Every leaf, however deep, selects through `choose`, so
 *  a submenu row keeps the menu's focus-return and after-close contract. */
function ActionMenuRows({ items, choose }: { items: readonly ActionMenuEntry[]; choose: (item: ActionMenuItem) => void }) {
  const { t } = useTranslation();
  return items.map((item, index) => {
    if (item === DIVIDER) return <DropdownMenuSeparator key={`divider-${index}`} />;
    const look = resolveMenuLook(item, t);
    const Icon = look.icon;
    const lead = look.iconNode ?? (Icon ? <Icon size={15} aria-hidden /> : null);
    const text = <span className="min-w-0 truncate" title={look.label}>{look.label}</span>;
    const key = item.id ?? look.label;
    if ('items' in item) {
      return (
        <DropdownMenuSub key={key}>
          <DropdownMenuSubTrigger disabled={item.disabled}>{lead}{text}</DropdownMenuSubTrigger>
          <DropdownMenuSubContent><ActionMenuRows items={item.items} choose={choose} /></DropdownMenuSubContent>
        </DropdownMenuSub>
      );
    }
    const variant = look.danger ? 'destructive' : 'default';
    if (item.checked !== undefined) {
      return (
        <DropdownMenuCheckboxItem key={key} variant={variant} disabled={item.disabled} checked={item.checked} onSelect={() => choose(item)}>
          {lead}{text}
        </DropdownMenuCheckboxItem>
      );
    }
    return (
      <DropdownMenuItem key={key} variant={variant} disabled={item.disabled} onSelect={() => choose(item)}>
        {lead}{text}
      </DropdownMenuItem>
    );
  });
}
